import { useEffect, useState } from "react";
import { RefreshCw, ExternalLink, Heart, Eye, Bookmark, MessageCircle } from "lucide-react";
import api from "../../lib/api";
import { photoUrl } from "./InstagramConnect";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const slotLabel = (s) => `${DAYS[s.weekday]} ${((s.hour + 11) % 12) + 1}${s.hour < 12 ? "am" : "pm"}`;
const n = (v) => (v == null ? "—" : Number(v).toLocaleString());

function Bars({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.avg_reach));
  return (
    <div className="rounded-xl border border-brand-border bg-brand-surface p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary mb-4">{title} · avg reach per post</p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.name}>
            <div className="flex justify-between text-xs mb-1"><span className="text-brand-ink capitalize">{r.name}</span><span className="text-brand-muted">{n(r.avg_reach)} · {r.avg_engagement} eng · {r.posts} posts</span></div>
            <div className="h-2 rounded-full bg-brand-hover overflow-hidden"><div className="h-full rounded-full bg-brand-gold" style={{ width: `${(r.avg_reach / max) * 100}%` }} /></div>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-brand-tertiary">No data yet.</p>}
      </div>
    </div>
  );
}

export function BestTimesCard() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/social/best-times", { params: { tz_offset_minutes: -new Date().getTimezoneOffset() } }).then(({ data }) => setB(data)).catch(() => {}); }, []);
  if (!b) return null;
  return (
    <div className="rounded-xl border border-brand-border bg-brand-ink text-brand-page p-5" data-testid="social-best-times">
      <p className="text-[10px] uppercase tracking-[0.14em] text-brand-page/60">Best times to post</p>
      <div className="flex flex-wrap gap-2 mt-3">{b.slots.map((s, i) => <span key={i} className="rounded-full bg-brand-page/10 px-3 py-1 text-sm">{slotLabel(s)}</span>)}</div>
      <p className="text-[11px] text-brand-page/60 mt-3">{b.label}</p>
    </div>
  );
}

export function SocialPerformance() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh) => { setBusy(true); api.get("/social/insights", { params: refresh ? { refresh: true } : {} }).then(({ data }) => setD(data)).finally(() => setBusy(false)); };
  useEffect(() => { load(false); }, []);
  if (!d) return <div className="h-40 rounded-xl bg-brand-surface border border-brand-border animate-pulse" />;
  const t = d.totals;
  return (
    <div className="space-y-6" data-testid="social-performance">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-brand-muted">{d.connected ? `${d.measured} of ${d.published} posts measured · stats refresh every 6 hours` : "Connect Instagram to start collecting likes, reach and saves."}</p>
        {d.connected && <button onClick={() => load(true)} disabled={busy} data-testid="social-insights-refresh" className="inline-flex items-center gap-2 rounded-full border border-brand-border px-3 py-1.5 text-xs"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh stats</button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[["Reach", t.reach], ["Views", t.views], ["Likes", t.likes], ["Saves", t.saved], ["Engagement rate", d.engagement_rate == null ? "—" : `${d.engagement_rate}%`]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-brand-border bg-brand-surface p-4" data-testid={`social-kpi-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary">{l}</p>
            <p className="font-serif text-3xl text-brand-ink mt-1">{typeof v === "number" ? n(v) : v}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Bars title="By location" rows={d.by_location} testid="social-by-location" />
        <Bars title="By theme" rows={d.by_theme} testid="social-by-theme" />
        <Bars title="By format" rows={d.by_format} testid="social-by-format" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary mb-3">Posted</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="social-posted-grid">
          {d.posts.map((p) => (
            <a key={p.id} href={p.permalink || "#"} target="_blank" rel="noreferrer" className="group rounded-lg overflow-hidden border border-brand-border bg-brand-surface" data-testid={`social-posted-${p.id}`}>
              <div className="relative aspect-[4/5]"><img src={photoUrl(p.photo_ids[0], true)} alt="" className="h-full w-full object-cover" /><ExternalLink className="absolute top-2 right-2 h-3.5 w-3.5 text-white opacity-0 group-hover:opacity-100" /></div>
              <div className="p-2 grid grid-cols-2 gap-1 text-[11px] text-brand-muted">
                <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{n(p.metrics?.reach)}</span>
                <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3" />{n(p.metrics?.likes)}</span>
                <span className="inline-flex items-center gap-1"><Bookmark className="h-3 w-3" />{n(p.metrics?.saved)}</span>
                <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" />{n(p.metrics?.comments)}</span>
              </div>
            </a>
          ))}
          {!d.posts.length && <p className="text-sm text-brand-tertiary col-span-full">Nothing posted yet.</p>}
        </div>
      </div>
    </div>
  );
}
